// M4 workstream C (world) view models — re-exported through src/sim/selectors.js (names unique across workstreams).
// All are pure reads (never create state.m4w) and JSON-safe; before 2000 they report the inactive defaults.
import { REGIONS, fieldDef } from '../defs.js';
import { SANCTIONS } from '../../data/m4/regions.js';
import { m4On, peekW } from './worldCore.js';
import { regimeStatusM4 } from './worldRegime.js';
import { megaprojectsM4View } from './worldMega.js';
import { regionWorldView, sanctionsFor } from './worldPolitics.js';
import { oilSandsView } from './worldOilSands.js';
import { deepwaterWellView } from './worldDeep.js';
import { eraM4View, quickStartM4Options } from './worldQuickStart.js';

// §11 well-control regime & permits for a region / water depth (moratorium, permit wait, missing kit, cost per well,
// BOP fail multiplier, the player's deepwater blowouts with their liability).
export const wellControlRegimeView = (state, regionId = 'gulf_of_mexico', waterFt = 5000) => regimeStatusM4(state, regionId, waterFt);
// §10 megaproject list with phase, first-oil day, share, cash calls, income, join quote.
export const megaprojectsM4List = (state) => megaprojectsM4View(state);
// §12 sanctions / nationalizations / capital controls / risk for one region.
export const worldRegionView = (state, regionId) => regionWorldView(state, regionId);
// §12 every region with a sanction or M4 nationalization in force for this company today.
export function sanctionsOverviewView(state) {
  if (!m4On(state)) return [];
  const rids = new Set(SANCTIONS.flatMap((s) => s.regionIds));
  return [...rids].filter((r) => REGIONS[r] && sanctionsFor(state, r).length).map((r) => regionWorldView(state, r));
}
// §15 oil-sands / extra-heavy operations at a field (units, blocks, tailings liability, today's flows, SOR, prices).
export const oilSandsOpsView = (state, fieldId) => oilSandsView(state, fieldId);
// §10 one well's deepwater status (suspension, BOP pull, subsea processing, CO₂ cut, H₂S, icebreaker).
export const deepwaterStatusView = (state, wellId) => deepwaterWellView(state, wellId);
// §17 the Era V transition card & Quick Start choices.
export const eraTransitionM4View = (state) => eraM4View(state);
export const quickStartM4List = () => quickStartM4Options();
// One summary for the HUD / ventures screen.
export function worldM4Summary(state) {
  const W = peekW(state);
  const on = m4On(state);
  const gulf = on ? regimeStatusM4(state, 'gulf_of_mexico', 5000) : null;
  return {
    active: on,
    moratorium: gulf?.moratorium ?? null,
    permitsPending: gulf ? gulf.permits.filter((p) => !p.ready).length : 0,
    megaprojectShares: Object.values(W?.mega ?? {}).filter((s) => s.share > 0).map((s) => ({ id: s.id, share: s.share, paid: Math.round(s.paid), income: Math.round(s.income) })),
    sanctionedRegions: on ? sanctionsOverviewView(state).map((r) => r.regionId) : [],
    oilSandsFields: Object.keys(W?.oilSands ?? {}).filter((f) => fieldDef(f)),
    trappedPesos: Math.round(Object.values(W?.pesos ?? {}).reduce((a, b) => a + b, 0)),
    openBlowouts: Object.values(W?.blowouts ?? {}).filter((b) => b.open).length,
  };
}
